/* eslint-disable ember/no-computed-properties-in-native-classes */
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { concat, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action, computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismLoadingBar,
  PrismMessage,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { and, not } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import { COMPSETS } from 'frontend-utils/utils/constants/constant-values';
import errorParse from 'frontend-utils/utils/error-parse';
import SettingsCompetitorsCompetitorListContent from 'lumen/components/settings/competitors/competitor-list-content';
import SettingsCompetitorsModalAddCompetitor from 'lumen/components/settings/competitors/modal-add-competitor';

import styles from './competitor-list.module.css';

interface SettingsCompetitorsCompetitorListSignature {
  Args: {
    canEditCurrentCompset: unknown;
    compsets: unknown;
    hasRmsIntegration: unknown;
    hotel: unknown;
    isLoading: unknown;
    isRMSTabActive: unknown;
    isSecondaryCompsetVisible: unknown;
    loadSelectedCompset: unknown;
    locale: unknown;
    onCancel: unknown;
    onCompetitorsChange: unknown;
    onSuccess: unknown;
    setIsLoading: unknown;
  };
}

export default class SettingsCompetitorsCompetitorList extends Component<SettingsCompetitorsCompetitorListSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service('smart-insights')
  declare smartInsights: Services['smart-insights'];

  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isAddCompetitorModalVisible = false;

  @computed(
    'access.{isPricingAssistantStandaloneVersion,hasMarketInsight}',
    'args.compsets.isRMS',
    'args.hotel.{totalCompetitorsUpperLimit,isTripadvisorRebranding}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canUpgradeCompset() {
    if (
      this.access.isPricingAssistantStandaloneVersion ||
      this.access.hasMarketInsight
    ) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const disallow =
      // @ts-expect-error: Incorrect type
      this.args.compsets.isRMS ||
      // @ts-expect-error: Incorrect type
      this.args.hotel.totalCompetitorsUpperLimit === 20 ||
      // @ts-expect-error: Incorrect type
      this.args.hotel.isTripadvisorRebranding;

    return !disallow;
  }

  @computed(
    'args.compsets.{isRMS,uniqueCompetitors.[]}',
    'args.hotel.{competitorsUpperLimit,totalCompetitorsUpperLimit}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get competitorsLimitReached() {
    // @ts-expect-error: Incorrect type
    if (this.args.compsets.isRMS) {
      // Check the competitorsUpperLimit for the RMS competitors
      return (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.compsets?.uniqueCompetitors?.length >=
        // @ts-expect-error: Incorrect type
        this.args.hotel?.competitorsUpperLimit
      );
    }
    // Check the totalCompetitorsUpperLimit for the primary and secondary competitors
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.compsets?.uniqueCompetitors?.length >=
      // @ts-expect-error: Incorrect type
      this.args.hotel?.totalCompetitorsUpperLimit
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAddCompetitorDisabled() {
    if (this.competitorsLimitReached) {
      return true;
    }

    if (this.args.isRMSTabActive) {
      return this.isRMSCompsetFull;
    }

    return this.isPrimaryCompsetFull && this.isSecondaryCompsetFull;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLastPrimaryCompetitor() {
    return this.numberOfCompetitorsPerCompset[COMPSETS.PRIMARY] === 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLastRMSCompetitor() {
    return this.numberOfCompetitorsPerCompset[COMPSETS.RMS] === 1;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.compsets.uniqueCompetitors.@each.isPrimary',
    'args.hotel.competitorsUpperLimit',
    'numberOfCompetitorsPerCompset',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPrimaryCompsetFull() {
    return (
      // @ts-expect-error: Incorrect type
      this.numberOfCompetitorsPerCompset[COMPSETS.PRIMARY] >=
      // @ts-expect-error: Incorrect type
      this.args.hotel?.competitorsUpperLimit
    );
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.compsets.uniqueCompetitors.@each.isRMS',
    'args.hotel.competitorsUpperLimit',
    'numberOfCompetitorsPerCompset',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRMSCompsetFull() {
    return (
      // @ts-expect-error: Incorrect type
      this.numberOfCompetitorsPerCompset[COMPSETS.RMS] >=
      // @ts-expect-error: Incorrect type
      this.args.hotel?.competitorsUpperLimit
    );
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.compsets.uniqueCompetitors.@each.isSecondary',
    'args.hotel.competitorsUpperLimit',
    'numberOfCompetitorsPerCompset',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSecondaryCompsetFull() {
    return (
      // @ts-expect-error: Incorrect type
      this.numberOfCompetitorsPerCompset[COMPSETS.SECONDARY] >=
      // @ts-expect-error: Incorrect type
      this.args.hotel?.competitorsUpperLimit
    );
  }

  @computed(
    'args.compsets.uniqueCompetitors.@each.{isPrimary,isSecondary,isRMS}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfCompetitorsPerCompset() {
    const numberOfCompetitorsPerCompset = {
      [COMPSETS.PRIMARY]: 0,
      [COMPSETS.SECONDARY]: 0,
      [COMPSETS.RMS]: 0,
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.compsets?.uniqueCompetitors?.forEach((competitor) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (competitor.isPrimary) {
        // @ts-expect-error: Incorrect type
        numberOfCompetitorsPerCompset[COMPSETS.PRIMARY] += 1;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (competitor.isSecondary) {
        // @ts-expect-error: Incorrect type
        numberOfCompetitorsPerCompset[COMPSETS.SECONDARY] += 1;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (competitor.isRMS) {
        // @ts-expect-error: Incorrect type
        numberOfCompetitorsPerCompset[COMPSETS.RMS] += 1;
      }
    });

    return numberOfCompetitorsPerCompset;
  }

  willDestroy(): void {
    super.willDestroy();

    // necessary cause these properties could stay stuck in the wrong state after a destroy
    // @ts-expect-error: Incorrect type
    this.args.setIsLoading(false);
    // @ts-expect-error: Incorrect type
    this.args.onCancel();
  }

  @action
  // @ts-expect-error: Incorrect type
  addCompetitor(hotelinfo, activeCompsets): void {
    // @ts-expect-error: Incorrect type
    set(this.args.compsets, 'uniqueCompetitors', [
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      ...this.args.compsets.uniqueCompetitors,
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        hotelinfo: { ...hotelinfo, id: hotelinfo.hotelinfo_id },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        isPrimary: activeCompsets.isPrimary,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        isSecondary: activeCompsets.isSecondary,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        isRMS: activeCompsets.isRMS,
      },
    ]);

    // @ts-expect-error: Incorrect type
    const translationKey = this.args.compsets.isRMS
      ? 'settings.competitors.competitor_added_to_rms_compset'
      : 'settings.competitors.competitor_added_to_compset';

    const toastMessage = this.intl.t(translationKey, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      competitor: hotelinfo.name,
    });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.save(toastMessage);
  }

  @action
  cancel(): void {
    // @ts-expect-error: Incorrect type
    this.args.loadSelectedCompset(this.args.isRMSTabActive);
  }

  @action
  closeAddCompetitorModal(): void {
    this.isAddCompetitorModalVisible = false;
  }

  @action
  // @ts-expect-error: Incorrect type
  highlight(competitor): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    set(competitor, 'highlighted', true);
  }

  @action
  async onSave(): Promise<void> {
    const customOptions = {
      text: this.intl.t('settings.competitors.warning.save').toString(),
    };

    if (this.args.isRMSTabActive) {
      // @ts-expect-error: Incorrect type
      customOptions.title = this.intl
        .t('settings.competitors.warning.save_rms.title')
        .toString();
      customOptions.text = this.intl
        .t('settings.competitors.warning.save_rms.text')
        .toString();
    }

    const confirmWasClicked =
      await this.alerting.showConfirmWarningAlert(customOptions);

    if (!confirmWasClicked) {
      return;
    }

    // @ts-expect-error: Incorrect type
    await this.save();
  }

  @action
  openAddCompetitorModal(): void {
    this.isAddCompetitorModalVisible = true;
  }

  @action
  // @ts-expect-error: Incorrect type
  reorderItems(reorderedCompetitors): void {
    set(this, 'args.compsets.uniqueCompetitors', reorderedCompetitors);
    // @ts-expect-error: Incorrect type
    this.args.onCompetitorsChange();
  }

  // @ts-expect-error: Incorrect type
  @action async save(successToastMessage): Promise<void> {
    // @ts-expect-error: Incorrect type
    this.args.setIsLoading(true);
    const compsets = [];

    if (this.args.isRMSTabActive) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const rmsCompset = this.args.compsets?.uniqueCompetitors
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .filter((competitor) => competitor.isRMS)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((comp) => comp.hotelinfo?.id);

      compsets.push({
        name: COMPSETS.RMS,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        competitors: rmsCompset,
      });
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const primaryCompset = this.args.compsets?.uniqueCompetitors
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .filter((competitor) => competitor.isPrimary)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((comp) => comp.hotelinfo?.id);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const secondaryCompset = this.args.compsets?.uniqueCompetitors
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .filter((competitor) => competitor.isSecondary)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .map((comp) => comp.hotelinfo?.id);

      compsets.push({
        name: COMPSETS.PRIMARY,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        competitors: primaryCompset,
      });

      compsets.push({
        name: COMPSETS.SECONDARY,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        competitors: secondaryCompset,
      });
    }

    const data = {
      hotel: {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        id: this.args.hotel?.id,
        compsets,
      },
    };

    // Determine if compsets are present
    const rmsChanged = compsets.some(
      (compset) => compset.name === COMPSETS.RMS,
    );
    const appPrimaryChanged = compsets.some(
      (compset) => compset.name === COMPSETS.PRIMARY,
    );
    const appSecondaryChanged = compsets.some(
      (compset) => compset.name === COMPSETS.SECONDARY,
    );

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const payload = await this.ajax.request('settings/compsets/', {
        method: 'POST',
        data: JSON.stringify(data),
      });

      this.usageAnalytics.track('Compset Settings Changed', {
        eventTrigger: 'settings-button',
        rmsChanged,
        appPrimaryChanged,
        appSecondaryChanged,
      });

      // This is necessary because ember does not update the store when an ajax request returns a payload
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload(payload);

      // Remove competitors that are not primary or secondary
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.compsets?.uniqueCompetitors?.forEach((competitor) => {
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !competitor.isPrimary &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !competitor.isSecondary &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !competitor.isRMS &&
          this.args.compsets
        ) {
          set(
            this.args.compsets,
            'uniqueCompetitors',
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            this.args.compsets.uniqueCompetitors.filter(
              // @ts-expect-error: Incorrect type
              (uniqueCompetitor) => uniqueCompetitor !== competitor,
            ),
          );
        }
      });

      // @ts-expect-error: Incorrect type
      this.args.onSuccess();

      const savedMessage =
        (successToastMessage as string | undefined) ??
        this.intl.t('settings.competitors.update_success');

      // Only the primary compset (non-RMS tab) feeds Smart Insights.
      const isReloadingInsights =
        !this.args.isRMSTabActive &&
        this.smartInsights.reloadAfterCompsetSave(savedMessage);

      if (!isReloadingInsights) {
        this.toast.success(savedMessage, {
          showIcon: true,
        });
      }

      if (isDestroyed(this) || isDestroying(this)) {
        return;
      }

      // @ts-expect-error: Incorrect type
      this.args.onCancel();
      // @ts-expect-error: Incorrect type
      this.args.setIsLoading(false);
    } catch (error) {
      // @ts-expect-error: Incorrect type
      const errors = errorParse(error, this.intl, true);

      this.errorTracking.captureException(error);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        title: this.intl.t('settings.competitors.update_error'),
        htmlContent: errors,
      });

      // @ts-expect-error: Incorrect type
      this.args.setIsLoading(false);
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  undoHighlight(competitor, isOption): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    set(competitor, 'highlighted', false);

    // Options are copies of uniqueCompetitors, propagate changes into original list
    if (isOption) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.compsets?.uniqueCompetitors?.forEach((uniqueCompetitor) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (uniqueCompetitor.hotelinfo?.id === competitor.hotelinfo?.id) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          set(uniqueCompetitor, 'highlighted', false);
        }
      });
    }
  }

  @action
  upgradeCompsetAction(): void {
    this.intercom.showNewMessage();
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div class="flex flex-1 flex-col overflow-hidden">
      {{#if
        (and
          @canEditCurrentCompset
          this.competitorsLimitReached
          this.canUpgradeCompset
        )
      }}
        <PrismBanner
          @bannerMessage={{t "settings.competitors.limit_reached.message"}}
          @bannerStyle="brand"
          @bannerTitle={{t "settings.competitors.limit_reached.title"}}
          @hasCta={{true}}
          @position="top"
          @primaryAction={{hash
            action=this.upgradeCompsetAction
            label=(t "settings.competitors.limit_reached.cta")
          }}
        />
      {{/if}}

      {{! @glint-expect-error: Incorrect type }}
      {{#if @compsets.uniqueCompetitors.length}}
        <section
          class={{concat
            styles.competitor-table
            " "
            "h-unset flex flex-1 flex-col overflow-hidden"
          }}
          id="competitors"
        >
          <div class={{styles.table-head}}>
            <div class="flex">
              <div
                class={{concat styles.cell " " styles.hotel-name " " "flex-1"}}
              >
                {{t "rates.hotelname"}}
              </div>
              <div class={{local styles "cell" "hotel-stars"}}>
                {{t "settings.competitors.rating"}}
              </div>
              <div class={{local styles "cell" "hotel-distance"}}>
                {{t "settings.competitors.table.distance"}}
              </div>
              {{#unless @isRMSTabActive}}
                <div class={{local styles "cell" "hotel-compset"}}>
                  {{t "settings.competitors.primary"}}
                </div>

                {{#if @isSecondaryCompsetVisible}}
                  <div class={{local styles "cell" "hotel-compset"}}>
                    {{t "settings.competitors.secondary"}}
                  </div>
                {{/if}}
              {{/unless}}
              {{#if @canEditCurrentCompset}}
                <div class={{local styles "cell" "hotel-delete"}}></div>
              {{/if}}
            </div>
          </div>
          {{#if @isLoading}}
            <div class="relative">
              <PrismLoadingBar />
            </div>
          {{/if}}

          {{! @glint-expect-error: Incorrect type }}
          {{#if @compsets.uniqueCompetitors.length}}
            <div class={{concat styles.content " " "flex-1"}}>
              {{#let "competitors" as |groupName|}}
                <ul
                  class="ic-product-tour-competitor-list"
                  id="competitorList"
                  {{sortableGroup
                    onChange=this.reorderItems
                    groupName=groupName
                  }}
                >
                  {{! @glint-expect-error: Incorrect type }}
                  {{#each @compsets.uniqueCompetitors as |competitor|}}
                    <li
                      class={{styles.row}}
                      data-test-id="competitor-list-content"
                      role="button"
                      {{on "mouseenter" (fn this.highlight competitor)}}
                      {{on "mouseleave" (fn this.undoHighlight competitor)}}
                      {{sortableItem
                        model=competitor
                        distance=5
                        groupName=groupName
                        disabled=(not @canEditCurrentCompset)
                      }}
                    >
                      <SettingsCompetitorsCompetitorListContent
                        {{! @glint-expect-error: Incorrect type }}
                        @activateSearch={{this.activateSearch}}
                        @canEditCurrentCompset={{@canEditCurrentCompset}}
                        @competitor={{competitor}}
                        @compsets={{@compsets}}
                        {{! @glint-expect-error: Incorrect type }}
                        @hasRmsIntegration={{@hasRmsIntegration}}
                        @hotel={{@hotel}}
                        @isAutocomplete={{false}}
                        @isLastPrimaryCompetitor={{this.isLastPrimaryCompetitor}}
                        @isLastRMSCompetitor={{this.isLastRMSCompetitor}}
                        @isLoading={{@isLoading}}
                        @isPrimaryCompsetFull={{this.isPrimaryCompsetFull}}
                        @isRMSCompsetFull={{this.isRMSCompsetFull}}
                        @isRMSTabActive={{@isRMSTabActive}}
                        @isSecondaryCompsetFull={{this.isSecondaryCompsetFull}}
                        @isSecondaryCompsetVisible={{@isSecondaryCompsetVisible}}
                        @locale={{@locale}}
                        @onCancel={{@onCancel}}
                        @onCompetitorsChange={{@onCompetitorsChange}}
                        @saveCompset={{this.save}}
                        @undoHighlight={{this.undoHighlight}}
                      />
                    </li>
                  {{/each}}
                </ul>
              {{/let}}
            </div>
          {{else}}
            <div class={{styles.message}}>
              {{! @glint-expect-error: Incorrect type }}
              {{#if (and @compsets.isRMS (not @hasRmsIntegration))}}
                <p class="text-300--regular">
                  {{t "settings.competitors.rms_support"}}
                </p>
              {{else}}
                <p class="text-300--regular">
                  {{t "settings.competitors.empty_compset"}}
                </p>
              {{/if}}
            </div>
          {{/if}}

          {{#if @canEditCurrentCompset}}
            <div class="flex items-center justify-end gap-200 px-400 py-200">
              {{! @glint-expect-error: Incorrect type }}
              {{#if @compsets.isModified}}
                <PrismButton
                  @buttonType="secondary"
                  {{! @glint-expect-error: Incorrect type }}
                  @isDisabled={{@isLoading}}
                  @label={{t "general.cancel"}}
                  @onClick={{this.cancel}}
                  data-test-id="button-cancel"
                />
                <PrismButton
                  {{! @glint-expect-error: Incorrect type }}
                  @isDisabled={{@isLoading}}
                  @label={{t "general.save"}}
                  @onClick={{this.onSave}}
                  data-event-id="settings-compset-save"
                  data-test-id="button-save"
                />
              {{else}}
                <div>
                  <PrismButton
                    @iconBefore="plus"
                    @isDisabled={{this.isAddCompetitorDisabled}}
                    @label={{t "settings.competitors.add_competitor"}}
                    @onClick={{this.openAddCompetitorModal}}
                    data-test-id="button-add-competitor"
                  />
                  {{#if this.isAddCompetitorDisabled}}
                    <PrismTooltip
                      @text={{t "settings.competitors.global_limit_reached"}}
                    />
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}
        </section>
      {{else}}
        <div class="flex h-full items-center justify-center">
          {{#if @canEditCurrentCompset}}
            <PrismMessage
              @illustration="100x100_warning"
              @title={{t "settings.competitors.empty_compset.title"}}
            >
              <:primaryAction>
                <PrismButton
                  @iconBefore="plus"
                  @isDisabled={{this.isAddCompetitorDisabled}}
                  @label={{t "settings.competitors.add_competitor"}}
                  @onClick={{this.openAddCompetitorModal}}
                  data-test-id="button-add-competitor"
                />
              </:primaryAction>
            </PrismMessage>
          {{else}}
            <PrismMessage
              @illustration="100x100_warning"
              @title={{t "settings.competitors.empty_compset.title"}}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>

    {{#if this.isAddCompetitorModalVisible}}
      <SettingsCompetitorsModalAddCompetitor
        @addCompetitor={{this.addCompetitor}}
        @compsets={{@compsets}}
        @isPrimaryCompsetFull={{this.isPrimaryCompsetFull}}
        @isRMSTabActive={{@isRMSTabActive}}
        @isSecondaryCompsetFull={{this.isSecondaryCompsetFull}}
        @isSecondaryCompsetVisible={{@isSecondaryCompsetVisible}}
        @onClose={{this.closeAddCompetitorModal}}
      />
    {{/if}}
  </template>
}
